import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import { api } from './api';
import { fmtDate, weekStart, addDays, weekDays, weekRangeLabel, dayLabel } from './dates';
import ImportUpwork from './ImportUpwork';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const COLORS   = ['#378ADD','#1D9E75','#D85A30','#7F77DD','#BA7517','#D4537E'];
const DAY_LTRS = ['M','T','W','R','F','S','S'];
// Day-grid rows: the 8 AM–5 PM working window (start hours 8..17).
const GRID_HOURS = Array.from({ length: 10 }, (_, k) => 8 + k);

// "8a", "12p", "1p", "5p" — compact 12-hour label for an integer hour.
function hourLabel(h) {
  const ampm = h < 12 ? 'a' : 'p';
  const h12  = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${ampm}`;
}

// Which integer hours does an entry occupy? start_hour through start_hour+hours
// (rounded up, at least 1). Returns [] for unscheduled entries.
function entryHours(e) {
  if (e.start_hour === null || e.start_hour === undefined) return [];
  const span = Math.max(1, Math.ceil(Number(e.hours)));
  const out = [];
  for (let h = e.start_hour; h < e.start_hour + span; h++) out.push(h);
  return out;
}

// Turn raw API error text into something a user can act on. The server's
// generic 500 ("Internal server error") is meaningless to a freelancer — map it
// to a plain-English retry prompt and keep specific validation messages as-is.
function friendlyError(msg) {
  if (!msg || /internal server error/i.test(msg)) {
    return "Couldn't load your data just now. Please refresh — if it keeps happening, sign out and back in.";
  }
  return msg;
}

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="#e57373" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

// ── Inline editable cell ─────────────────────────────────────────────────────
function EditableCell({ value, field, options, onSave, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(value);

  function start() { setDraft(value); setEditing(true); }
  function save()  {
    setEditing(false);
    if (String(draft) !== String(value)) onSave(draft);
  }

  if (editing) {
    if (options) {
      return (
        <select className="cell-select" value={draft} autoFocus
          onChange={e => setDraft(e.target.value)} onBlur={save}>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    return (
      <input
        className="cell-input"
        type={field === 'hours' ? 'number' : field === 'entry_date' ? 'date' : 'text'}
        step={field === 'hours' ? '0.5' : undefined}
        style={field === 'hours' ? { width: 60, textAlign: 'right' } : { width: '100%' }}
        value={draft} autoFocus
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
      />
    );
  }

  return (
    <span className={`editable ${className}`} onClick={start}>{value}</span>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function TimeTracker({ projects, onProjectsChange }) {
  const [monday,  setMonday]  = useState(weekStart());
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);  // open invoices across all projects
  const [filter,  setFilter]  = useState({ type: null, value: null });
  const [error,   setError]   = useState('');
  const [showImport, setShowImport] = useState(false);
  const [loading, setLoading] = useState(true);   // distinguishes "loading" from "empty week"
  const [view,    setView]    = useState('table'); // 'table' | 'grid'
  const [undo,    setUndo]    = useState(null);     // { id } of last-added entry, for the toast
  const [pendingDelete, setPendingDelete] = useState(null); // entry id awaiting inline confirm

  // Add-entry form
  const [desc,   setDesc]   = useState('');
  const [projId, setProjId] = useState('');
  const [date,   setDate]   = useState(fmtDate(new Date()));
  const [hours,  setHours]  = useState('');
  const [invId,  setInvId]  = useState('');
  const [startHour, setStartHour] = useState(''); // '' = unscheduled
  const descRef = useRef(null);                    // refocused after each add (keyboard-first)

  const days = weekDays(monday);

  // ── Load entries ────────────────────────────────────────────────────────
  const loadEntries = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const data = await api.getEntries(days[0], days[6]);
      setEntries(data);
    } catch (err) {
      setError(friendlyError(err.message));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line
  }, [monday]);

  // ── Load open invoices ──────────────────────────────────────────────────
  async function loadInvoices() {
    try {
      setInvoices(await api.getInvoices(null, true));
    } catch (err) {
      // non-critical
    }
  }

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => { loadInvoices(); }, []);

  // Set default project when projects load
  useEffect(() => {
    if (projects.length && !projId) setProjId(String(projects[0].id));
  }, [projects]); // eslint-disable-line

  // Reset invoice when project changes
  useEffect(() => { setInvId(''); }, [projId]);

  // Current selected project
  const currentProject = projects.find(p => String(p.id) === String(projId));
  const isPlatformWork = currentProject?.is_platform_work;

  // Invoices for the currently selected project
  const projectInvoices = invoices.filter(i => String(i.project_id) === String(projId));

  // ── Navigation ──────────────────────────────────────────────────────────
  function prevWeek() { setMonday(m => addDays(m, -7)); setFilter({ type: null, value: null }); }
  function nextWeek() { setMonday(m => addDays(m, 7));  setFilter({ type: null, value: null }); }

  // ── Duplicate last week ─────────────────────────────────────────────────
  // Copies every entry from the previous Mon-Sun window into this week,
  // +7 days. Preserves project, description, hours, rate/fee overrides.
  // Drops invoice assignment — those almost always change week to week.
  async function handleDuplicateLastWeek() {
    const lastWeekMon = fmtDate(addDays(monday, -7));
    const hasEntries  = entries.length > 0;
    const confirmMsg  = hasEntries
      ? `This week already has ${entries.length} entries. Duplicate last week on top? (Adds new entries; doesn't delete existing ones.)`
      : 'Copy every entry from last week into this week?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const res = await api.duplicateWeek(lastWeekMon);
      if (res.copied === 0) {
        setError('No entries found in last week — nothing to copy.');
        return;
      }
      setError('');
      loadEntries();
      loadInvoices();
      if (res.errors && res.errors.length > 0) {
        setError(`Copied ${res.copied} of ${res.source_count} entries; ${res.errors.length} failed.`);
      }
    } catch (err) {
      setError(friendlyError(err.message));
    }
  }

  // ── Add entry ────────────────────────────────────────────────────────────
  async function handleAdd(e) {
    e.preventDefault();
    if (!projId) return setError('Please select a project');
    if (!hours || parseFloat(hours) <= 0) return setError('Please enter valid hours');
    setError('');
    try {
      const created = await api.createEntry({
        project_id:  parseInt(projId),
        description: desc.trim() || '—',
        entry_date:  date,
        hours:       parseFloat(hours),
        invoice_id:  invId ? parseInt(invId) : null,
        start_hour:  startHour === '' ? null : parseInt(startHour),
      });
      setDesc(''); setHours(''); setInvId('');
      // Keyboard-first: clear and refocus Description so the next row is one keystroke away.
      if (descRef.current) descRef.current.focus();
      // Brief "Added ✓ — Undo" affordance.
      if (created && created.id) {
        setUndo({ id: created.id });
        setTimeout(() => setUndo(u => (u && u.id === created.id ? null : u)), 6000);
      }
      loadEntries();
      loadInvoices();
    } catch (err) {
      setError(friendlyError(err.message));
    }
  }

  // Undo the most recent add (deletes the just-created entry).
  async function handleUndoAdd() {
    if (!undo) return;
    const id = undo.id;
    setUndo(null);
    try {
      await api.deleteEntry(id);
      loadEntries();
      loadInvoices();
    } catch (err) {
      setError(friendlyError(err.message));
    }
  }

  // ── Delete / inline save ─────────────────────────────────────────────────
  // Two-step inline confirm (no jarring window.confirm): first click arms
  // pendingDelete, the row shows Yes/No, second click commits.
  async function handleDelete(id) {
    try {
      await api.deleteEntry(id);
      setEntries(prev => prev.filter(e => e.id !== id));
      setPendingDelete(null);
      loadInvoices();
    } catch (err) {
      setError(friendlyError(err.message));
    }
  }

  async function handleSave(id, field, value) {
    const original = entries.find(e => e.id === id);
    if (!original) return;

    // Optimistic update
    setEntries(prev => prev.map(e => {
      if (e.id !== id) return e;
      if (field === 'project_id') {
        const p = projects.find(p => p.id === parseInt(value));
        return { ...e, project_id: parseInt(value), project_name: p?.name || e.project_name };
      }
      return { ...e, [field]: value };
    }));

    try {
      const body = {};
      if (field === 'project_id')  body.project_id   = parseInt(value);
      if (field === 'hours')       body.hours        = parseFloat(value);
      if (field === 'entry_date')  body.entry_date   = value;
      if (field === 'description') body.description  = value;
      await api.updateEntry(id, body);
      loadEntries();
      loadInvoices();
    } catch (err) {
      setError(friendlyError(err.message));
      setEntries(prev => prev.map(e => e.id === id ? original : e));
    }
  }

  // ── Filter ───────────────────────────────────────────────────────────────
  function toggleFilter(type, value) {
    setFilter(f => (f.type === type && f.value === value) ? { type: null, value: null } : { type, value });
  }
  function clearFilter() { setFilter({ type: null, value: null }); }

  const filtered = filter.type === 'day'
    ? entries.filter(e => e.entry_date === filter.value)
    : filter.type === 'project'
    ? entries.filter(e => e.project_name === filter.value)
    : entries;

  // ── Chart data ────────────────────────────────────────────────────────────
  const dayTotals = days.map(d =>
    entries.filter(e => e.entry_date === d).reduce((s, e) => s + Number(e.hours), 0)
  );

  const projMap = {};
  entries.forEach(e => { projMap[e.project_name] = (projMap[e.project_name] || 0) + Number(e.hours); });
  const projNames  = Object.keys(projMap);
  const projHours  = projNames.map(n => projMap[n]);

  const barBg = days.map((d, i) => {
    if (filter.type === 'day') return d === filter.value ? '#378ADD' : 'rgba(55,138,221,0.18)';
    return i === 6 ? '#378ADD' : 'rgba(55,138,221,0.45)';
  });

  const pieBg = projNames.map((n, i) => {
    if (filter.type === 'project') return n === filter.value ? COLORS[i % COLORS.length] : 'rgba(150,150,150,0.18)';
    return COLORS[i % COLORS.length];
  });

  // ── Totals ────────────────────────────────────────────────────────────────
  const totalHours  = filtered.reduce((s, e) => s + Number(e.hours), 0);
  const totalAmount = Math.round(filtered.reduce((s, e) => s + Number(e.amount || 0), 0));
  const totalLabel  = filter.type ? 'Filtered' : 'Week';

  // Project dropdown options for inline edit
  const projOptions = projects.map(p => ({ value: String(p.id), label: p.name }));

  // Stable colour per project id (index into COLORS) so the same customer keeps
  // the same colour across the day grid and the doughnut.
  const projColor = {};
  projects.forEach((p, i) => { projColor[p.id] = COLORS[i % COLORS.length]; });

  // Weekly-total cue: freelancers watch the weekly number. >40h reads as a full
  // load (green), <10h as light (muted), in-between neutral.
  const weekClass = !filter.type
    ? (totalHours >= 40 ? ' wk-high' : totalHours > 0 && totalHours < 10 ? ' wk-low' : '')
    : '';

  // Clicking an empty grid slot seeds the Add-entry form for that day + hour.
  function seedFromSlot(day, hour) {
    setDate(day);
    setStartHour(String(hour));
    if (!hours) setHours('1');
    if (descRef.current) descRef.current.focus();
  }

  return (
    <div>
      {/* ── Week nav + action buttons ── */}
      <div className="week-nav">
        <button className="nav-btn" onClick={prevWeek}>← Prev</button>
        <span className="week-label">{weekRangeLabel(monday)}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" onClick={handleDuplicateLastWeek} title="Copy all entries from the previous week into this week">
            ⎘ Duplicate last week
          </button>
          <button className="btn-outline" onClick={() => setShowImport(true)}>↑ Import Upwork CSV</button>
          <button className="nav-btn" onClick={nextWeek}>Next →</button>
        </div>
      </div>

      {/* ── View toggle: flat table vs hour-by-hour day grid ── */}
      <div className="view-toggle">
        <button className={`vt-btn${view === 'table' ? ' active' : ''}`} onClick={() => setView('table')}>☰ Table</button>
        <button className={`vt-btn${view === 'grid'  ? ' active' : ''}`} onClick={() => setView('grid')}>▦ Day grid</button>
      </div>

      {/* ── Day header strip (click a day to set the Add-entry date) ── */}
      <div className="day-header">
        {days.map((d, i) => (
          <div
            key={d}
            className={`day-cell${d === fmtDate(new Date()) ? ' today' : ''}${d === date ? ' selected' : ''}`}
            onClick={() => setDate(d)}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDate(d); } }}
            title="Use this day for the new entry"
          >
            <div className="dl">{DAY_LTRS[i]}</div>
            <div className="dn">{d.slice(8)}</div>
            <div className="dt">{dayTotals[i] > 0 ? `${dayTotals[i].toFixed(dayTotals[i] % 1 ? 1 : 0)}h` : ''}</div>
          </div>
        ))}
      </div>

      {/* ── No projects yet: the Add-entry form is unusable without one ── */}
      {projects.length === 0 && (
        <div className="empty-projects">
          <div className="empty-projects-icon">📁</div>
          <div>
            <strong>No projects yet.</strong> Create a project first — then you can log time against it.
          </div>
          {onProjectsChange && (
            <p className="empty-projects-hint">Head to the <strong>Projects</strong> tab to add your first one.</p>
          )}
        </div>
      )}

      {/* ── Add entry form ── */}
      {projects.length > 0 && (
      <div className="card">
        <div className="form-title">Add time entry</div>
        <form onSubmit={handleAdd}>
          <div className={`form-row${!isPlatformWork ? ' with-invoice' : ''}`}>
            <div className="field">
              <label>Description</label>
              <input ref={descRef} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What did you work on?" />
            </div>
            <div className="field">
              <label>Project</label>
              <select value={projId} onChange={e => setProjId(e.target.value)}>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div className="field">
              <label>Start</label>
              <select value={startHour} onChange={e => setStartHour(e.target.value)} title="Time of day — used by the Day grid to spot double-bookings">
                <option value="">— anytime —</option>
                {Array.from({ length: 10 }, (_, k) => 8 + k).map(h => (
                  <option key={h} value={h}>{hourLabel(h)}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Hours</label>
              <input
                type="number" step="0.5" min="0.5" max="24"
                value={hours} onChange={e => setHours(e.target.value)} placeholder="0.0"
              />
              <div className="hour-presets">
                {[1, 2, 4, 8].map(h => (
                  <button key={h} type="button" className="hour-chip"
                    onClick={() => setHours(String(h))} title={`Set ${h} hours`}>
                    {h}h
                  </button>
                ))}
              </div>
            </div>
            {!isPlatformWork && (
              <div className="field">
                <label>Invoice</label>
                <select value={invId} onChange={e => setInvId(e.target.value)}>
                  <option value="">— unassigned —</option>
                  {projectInvoices.map(i => (
                    <option key={i.id} value={i.id}>
                      {i.invoice_number} · {Number(i.hours_remaining).toFixed(1)} of {Number(i.hours_purchased).toFixed(0)} hrs left
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label>&nbsp;</label>
              <button className="btn-primary" type="submit">+ Add Entry</button>
            </div>
          </div>
          {!isPlatformWork && projectInvoices.length === 0 && (
            <div className="info-note">
              No open invoice for this project — entry will be saved unassigned. Create one in the <strong>Invoices</strong> tab.
            </div>
          )}
          {error && <p className="error-msg" style={{ marginTop: 8 }}>{error}</p>}
        </form>
      </div>
      )}

      {/* Surface load errors even when the Add form is hidden (no projects). */}
      {projects.length === 0 && error && (
        <p className="error-msg" style={{ marginTop: 8 }}>{error}</p>
      )}

      {/* ── Charts (table view only) ── */}
      {view === 'table' && entries.length > 0 && (
        <div className="charts-row">
          <div className={`chart-card${filter.type === 'day' ? ' filtered' : ''}`}>
            <div className="chart-top">
              <span className="chart-label">Hours by day</span>
              {filter.type === 'day' && <button className="clear-chip" onClick={clearFilter}>✕ Clear</button>}
            </div>
            <div className="chart-canvas">
              <Bar
                data={{ labels: DAY_LTRS, datasets: [{ data: dayTotals, backgroundColor: barBg, borderRadius: 4, borderSkipped: false }] }}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ctx.parsed.y + ' hrs' } } },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#737370', font: { size: 12 } } },
                    y: { grid: { color: 'rgba(0,0,0,0.06)' }, ticks: { color: '#737370', font: { size: 11 }, stepSize: 1 }, beginAtZero: true },
                  },
                  onClick: (_, els) => { if (els.length) toggleFilter('day', days[els[0].index]); },
                  onHover: (evt, els) => { evt.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
                }}
              />
            </div>
          </div>

          <div className={`chart-card${filter.type === 'project' ? ' filtered' : ''}`}>
            <div className="chart-top">
              <span className="chart-label">Time by project</span>
              {filter.type === 'project' && <button className="clear-chip" onClick={clearFilter}>✕ Clear</button>}
            </div>
            <div className="chart-canvas">
              <Doughnut
                data={{ labels: projNames, datasets: [{ data: projHours, backgroundColor: pieBg, borderWidth: 0, hoverOffset: 4 }] }}
                options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right', labels: { color: '#737370', font: { size: 12 }, boxWidth: 12, padding: 10 } },
                    tooltip: { callbacks: { label: ctx => {
                      const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                      return `${ctx.label}: ${ctx.parsed.toFixed(1)} hrs (${Math.round(ctx.parsed / total * 100)}%)`;
                    }}},
                  },
                  onClick: (_, els) => { if (els.length) toggleFilter('project', projNames[els[0].index]); },
                  onHover: (evt, els) => { evt.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Filter badge ── */}
      {filter.type && (
        <div className="filter-badge">
          {filter.type === 'day' ? `Day: ${dayLabel(filter.value)}` : `Project: ${filter.value}`}
          <button onClick={clearFilter}>✕</button>
        </div>
      )}

      {/* ── Day grid view (hour-by-hour, conflict-aware) ── */}
      {view === 'grid' && (
        <DayGrid
          days={days} entries={entries} projColor={projColor}
          loading={loading} onSlotClick={seedFromSlot}
        />
      )}

      {/* ── Entries table view ── */}
      {view === 'table' && (
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: 95 }}>Day</th>
              <th style={{ width: 155 }}>Project</th>
              <th>Description</th>
              <th style={{ width: 110 }}>Invoice</th>
              <th style={{ width: 65, textAlign: 'right' }}>Hours</th>
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && entries.length === 0 && (
              [0, 1, 2].map(i => (
                <tr key={`sk${i}`} className="skeleton-row">
                  <td colSpan={6}><div className="skeleton-bar" /></td>
                </tr>
              ))
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#737370', padding: '1.5rem' }}>
                No entries this week — add one above
              </td></tr>
            )}
            {entries.map(e => {
              const visible =
                !filter.type ||
                (filter.type === 'day'     && e.entry_date   === filter.value) ||
                (filter.type === 'project' && e.project_name === filter.value);

              return (
                <tr key={e.id} className={visible ? '' : 'dimmed'}>
                  <td style={{ verticalAlign: 'top' }}>
                    <EditableCell
                      value={e.entry_date}
                      field="entry_date"
                      options={days.map(d => ({ value: d, label: dayLabel(d) }))}
                      onSave={v => handleSave(e.id, 'entry_date', v)}
                      className="day-badge"
                    />
                  </td>
                  <td style={{ verticalAlign: 'top' }}>
                    <EditableCell
                      value={String(e.project_id)}
                      field="project_id"
                      options={projOptions}
                      onSave={v => handleSave(e.id, 'project_id', v)}
                    />
                  </td>
                  <td className="desc-cell">
                    <EditableCell
                      value={e.description}
                      field="description"
                      onSave={v => handleSave(e.id, 'description', v)}
                    />
                  </td>
                  <td style={{ fontSize: 12, color: e.invoice_number ? '#1a1a1a' : '#a8a8a3' }}>
                    {e.is_platform_work
                      ? <span style={{ fontStyle: 'italic' }}>platform</span>
                      : (e.invoice_number || <span style={{ fontStyle: 'italic' }}>unassigned</span>)}
                  </td>
                  <td className="hours-cell">
                    <EditableCell value={e.hours} field="hours" onSave={v => handleSave(e.id, 'hours', v)} />
                  </td>
                  <td style={{ textAlign: 'center', verticalAlign: 'top' }}>
                    {pendingDelete === e.id ? (
                      <span className="del-confirm">
                        <button className="del-yes" onClick={() => handleDelete(e.id)} title="Confirm delete">Yes</button>
                        <button className="del-no"  onClick={() => setPendingDelete(null)} title="Keep entry">No</button>
                      </span>
                    ) : (
                      <button className="trash-btn" onClick={() => setPendingDelete(e.id)} title="Delete entry">
                        <TrashIcon />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className={`total-row hours-row${weekClass}`}>
              <td colSpan={4}>{totalLabel} — hours</td>
              <td>{totalHours.toFixed(1)}</td>
              <td></td>
            </tr>
            <tr className="total-row dollar-row">
              <td colSpan={4}>{totalLabel} — earnings</td>
              <td>${totalAmount.toLocaleString()}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      )}

      {view === 'table' && (
        <p className="hint">Click any cell to edit inline. Invoice is auto-hidden for platform-work projects.</p>
      )}
      {view === 'grid' && (
        <p className="hint">Click an empty slot to log time there. Red cells are double-booked — two customers at the same hour.</p>
      )}

      {/* ── Undo toast (after an add) ── */}
      {undo && (
        <div className="undo-toast">
          Added ✓
          <button onClick={handleUndoAdd}>Undo</button>
        </div>
      )}

      {/* ── Import modal ── */}
      {showImport && (
        <ImportUpwork
          projects={projects}
          onClose={() => setShowImport(false)}
          onComplete={() => { loadEntries(); loadInvoices(); onProjectsChange && onProjectsChange(); }}
        />
      )}
    </div>
  );
}

// ── Day grid ─────────────────────────────────────────────────────────────────
// Hour-by-hour board: rows are the 8 AM–5 PM working window, columns are the
// week's M–S days. Each scheduled entry fills its hour span with the project's
// colour; two different projects in the same slot render as a red conflict so a
// double-booking ("8 AM for customer A AND customer B") is impossible to miss.
function DayGrid({ days, entries, projColor, loading, onSlotClick }) {
  // Index entries by "day|hour" → list of covering entries.
  const cell = {};
  entries.forEach(e => {
    entryHours(e).forEach(h => {
      const key = `${e.entry_date}|${h}`;
      (cell[key] ||= []).push(e);
    });
  });

  const anyScheduled = entries.some(e => entryHours(e).length > 0);

  return (
    <div className="day-grid-wrap">
      {loading && <div className="grid-loading">Loading…</div>}
      <div className="day-grid" role="grid">
        {/* Header row: blank corner + day labels */}
        <div className="dg-corner" />
        {days.map((d, i) => (
          <div key={d} className={`dg-dayhead${d === fmtDate(new Date()) ? ' today' : ''}`}>
            <span className="dg-dl">{DAY_LTRS[i]}</span> <span className="dg-dn">{d.slice(8)}</span>
          </div>
        ))}

        {/* One row per working hour */}
        {GRID_HOURS.map(h => (
          <FragmentRow key={h}>
            <div className="dg-hour">{hourLabel(h)}</div>
            {days.map(d => {
              const here = cell[`${d}|${h}`] || [];
              const ids  = [...new Set(here.map(x => x.project_id))];
              const conflict = ids.length > 1;
              if (here.length === 0) {
                return (
                  <button
                    key={d + h}
                    className="dg-slot dg-empty"
                    onClick={() => onSlotClick(d, h)}
                    title={`Log time on ${dayLabel(d)} at ${hourLabel(h)}`}
                  />
                );
              }
              const top = here[0];
              return (
                <div
                  key={d + h}
                  className={`dg-slot dg-filled${conflict ? ' dg-conflict' : ''}`}
                  style={conflict ? undefined : { background: projColor[top.project_id] || '#888' }}
                  title={conflict
                    ? `Double-booked: ${here.map(x => x.project_name).join(' + ')}`
                    : `${top.project_name} — ${Number(top.hours)}h${top.description && top.description !== '—' ? ` · ${top.description}` : ''}`}
                >
                  <span className="dg-proj">{conflict ? '⚠ conflict' : top.project_name}</span>
                </div>
              );
            })}
          </FragmentRow>
        ))}
      </div>

      {!anyScheduled && !loading && (
        <p className="grid-empty-note">
          No scheduled entries this week. Add an entry with a <strong>Start</strong> time, or click any slot above.
        </p>
      )}
    </div>
  );
}

// CSS-grid wants the row's cells as direct children, so a Fragment groups the
// hour label + 7 day slots without introducing a wrapping element.
function FragmentRow({ children }) {
  return <>{children}</>;
}
